import type { StaffFeatureId } from "@wallyo/contracts/actions";
import {
  APPROVAL_REQUEST_STATUSES,
  type ApprovalRequest,
  type ApprovalRequestStatus,
} from "@wallyo/contracts/console/payments";
import { permissionName, t } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { approvalBlock, type ApprovalActionKind } from "./payment-actions";
import { approvalSummary } from "./payment-format";
import { ALL_PAYMENTS, paymentsHref } from "./payment-route";

export type ApprovalsState =
  | { status: "loading"; requests: readonly ApprovalRequest[] }
  | { status: "ready"; requests: readonly ApprovalRequest[] }
  | { status: "error"; requests: readonly ApprovalRequest[]; error: unknown };

export type ApprovalsViewProps = {
  status: ApprovalRequestStatus;
  state: ApprovalsState;
  me: { staffMemberId: string; features: readonly StaffFeatureId[] };
  onRetry: () => void;
  onDecide: (request: ApprovalRequest, kind: ApprovalActionKind) => void;
};

const TONE: Record<ApprovalRequestStatus, string> = { pending: "warn", approved: "ok", rejected: "mute" };

type Summary = ReturnType<typeof approvalSummary>;

/** What the requester held when they asked (ADR-0019 §6). */
function TheirLimit({ held }: { held: Summary["held"] }) {
  if (held.kind === "none") return <span className="meta">{t("payments.approvals.noPermission")}</span>;
  if (held.kind === "noLimit") return <span className="meta">{t("payments.approvals.noLimitInCurrency")}</span>;
  return <span className="meta">{t("payments.approvals.theirLimit", { amount: held.amount })}</span>;
}

/** Who decided it, and why, once someone has. */
function Decision({ request }: { request: ApprovalRequest }) {
  if (!request.decidedBy) return null;
  let date = "";
  if (request.decidedAt) date = formatDateTime(request.decidedAt);
  let why = "";
  if (request.decisionReason) why = ` · ${request.decisionReason}`;
  return (
    <span className="meta">
      <br />
      {t("payments.approvals.decided", { name: request.decidedBy.name, date })}
      {why}
    </span>
  );
}

/** A refund or waiver links to the fee; a credit belongs to the pro, so it links to them. */
function TargetLink({ summary, status }: { summary: Summary; status: ApprovalRequestStatus }) {
  if (summary.platformFeePaymentId) {
    return (
      <>
        <br />
        <a className="go-link" href={paymentsHref(ALL_PAYMENTS, summary.platformFeePaymentId, status)}>
          {t("payments.approvals.openPayment")}
        </a>
      </>
    );
  }
  if (summary.workerId) {
    return (
      <>
        <br />
        <a className="go-link" href={paymentsHref({ ...ALL_PAYMENTS, workerId: summary.workerId }, null, status)}>
          {t("payments.approvals.openPro")}
        </a>
      </>
    );
  }
  return null;
}

function DecideButtons({
  request,
  block,
  onDecide,
}: {
  request: ApprovalRequest;
  block: ReturnType<typeof approvalBlock>;
  onDecide: ApprovalsViewProps["onDecide"];
}) {
  if (block !== null && block !== "own") return null;
  return (
    <>
      <button
        className="btn small primary"
        type="button"
        disabled={block === "own"}
        data-decide="approve"
        onClick={() => onDecide(request, "approve")}
      >
        {t("payments.action.approve")}
      </button>
      <button
        className="btn small"
        type="button"
        disabled={block === "own"}
        data-decide="reject"
        onClick={() => onDecide(request, "reject")}
      >
        {t("payments.action.reject")}
      </button>
    </>
  );
}

function OwnRequestNote({ block }: { block: ReturnType<typeof approvalBlock> }) {
  if (block !== "own") return null;
  return (
    <p className="foot" data-own="">
      {t("payments.approvals.own")}
    </p>
  );
}

function Request({
  request,
  status,
  me,
  onDecide,
}: {
  request: ApprovalRequest;
  status: ApprovalRequestStatus;
  me: ApprovalsViewProps["me"];
  onDecide: ApprovalsViewProps["onDecide"];
}) {
  const summary = approvalSummary(request);
  const block = approvalBlock(request, me);
  let amount = "";
  if (summary.amount) amount = ` · ${summary.amount}`;
  return (
    <div className="approval" data-approval={request.approvalRequestId}>
      <span>
        <b>
          {t(`payments.kind.${summary.kind}` as const)}
          {amount}
        </b>
        <br />
        <span className="meta">
          {t("payments.approvals.requested", {
            name: request.requestedBy.name,
            date: formatDateTime(request.createdAt),
          })}
        </span>
        <br />
        <span className="meta">
          {t("payments.approvals.permission", {
            permission: permissionName(summary.permission),
          })}
        </span>
        <br />
        <TheirLimit held={summary.held} />
        <br />
        <span className="meta">{request.requestReason}</span>
        <Decision request={request} />
        <TargetLink summary={summary} status={status} />
      </span>
      <span className="verif-pills">
        <span className={`pill ${TONE[request.status]}`}>
          {t(`payments.approvals.status.${request.status}` as const)}
        </span>
        <DecideButtons request={request} block={block} onDecide={onDecide} />
      </span>
      <OwnRequestNote block={block} />
    </div>
  );
}

function Requests({ status, state, me, onRetry, onDecide }: ApprovalsViewProps) {
  if (state.status === "error") {
    return (
      <div className="audit-error">
        <p className="foot">{t("payments.approvals.loadFailed")}</p>
        <ErrorNotice error={state.error} />
        <div>
          <button className="btn primary" type="button" onClick={onRetry}>
            {t("audit.retry")}
          </button>
        </div>
      </div>
    );
  }
  if (state.status === "loading" && state.requests.length === 0) {
    return (
      <div className="empty" role="status">
        <span className="spinner" aria-hidden="true" />
        <span>{t("payments.approvals.loading")}</span>
      </div>
    );
  }
  if (state.requests.length === 0) {
    return (
      <p className="foot" role="status">
        {t("payments.approvals.empty")}
      </p>
    );
  }
  return (
    <div className="rows">
      {state.requests.map((request) => (
        <Request key={request.approvalRequestId} request={request} status={status} me={me} onDecide={onDecide} />
      ))}
    </div>
  );
}

function StatusTab({ status, current }: { status: ApprovalRequestStatus; current: ApprovalRequestStatus }) {
  const on = status === current;
  let className = "tab";
  if (on) className = "tab on";
  return (
    <a className={className} href={paymentsHref(ALL_PAYMENTS, null, status)} role="tab" aria-selected={on}>
      {t(`payments.approvals.status.${status}` as const)}
    </a>
  );
}

/**
 * What staff asked for and may not do alone (ADR-0019 §6): a refund, a credit
 * or a waiver beyond the permission they hold. Whoever holds that permission
 * within their own limit decides, and nobody decides their own request.
 */
export function ApprovalsView(props: ApprovalsViewProps) {
  return (
    <div className="card approvals">
      <div className="tabs" role="tablist">
        {APPROVAL_REQUEST_STATUSES.map((each) => (
          <StatusTab key={each} status={each} current={props.status} />
        ))}
      </div>
      <Requests {...props} />
    </div>
  );
}
